/** Page des pointages - filtres, liste, CRUD admin (créer/éditer/supprimer), export CSV. */

import { useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import { Camera, Download, Pencil, Trash2, Plus } from 'lucide-react';
import { Header } from '../components/layout/Header';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Modal } from '../components/ui/Modal';
import { Table, type Column } from '../components/ui/Table';
import { Pagination } from '../components/shared/Pagination';
import {
  useTimeLogs,
  useCreateTimeLog,
  useUpdateTimeLog,
  useDeleteTimeLog,
} from '../hooks/useTimeLogs';
import { useEmployees } from '../hooks/useEmployees';
import { useReaders } from '../hooks/useReaders';
import { timeLogsApi } from '../services/api';
import { formatDate, formatTime, parseServerDate } from '../lib/utils';
import { ACTIVITY_LABEL, type EmployeeActivity, type TimeLog } from '../types';

export function TimeLogsPage() {
  const today = format(new Date(), 'yyyy-MM-dd');

  const [page, setPage] = useState(1);
  const [dateFrom, setDateFrom] = useState(today);
  const [dateTo, setDateTo] = useState(today);
  const [employeeId, setEmployeeId] = useState<number | undefined>();
  const [readerId, setReaderId] = useState<number | undefined>();
  const [punchType, setPunchType] = useState<string | undefined>();
  const [teamFilter, setTeamFilter] = useState<EmployeeActivity | ''>('');

  // Modal édition / création
  const [editingLog, setEditingLog] = useState<TimeLog | null>(null);
  const [photoLog, setPhotoLog] = useState<TimeLog | null>(null);
  const [creating, setCreating] = useState(false);

  const { data, isLoading } = useTimeLogs({
    page,
    per_page: 20,
    date_from: dateFrom || undefined,
    date_to: dateTo || undefined,
    employee_id: employeeId,
    reader_id: readerId,
    type: punchType,
    activity: teamFilter || undefined,
  });

  const { data: employeesData } = useEmployees({ per_page: 100, active_only: false });

  // Filtre employé : actifs d'abord (filtrés par équipe le cas échéant),
  // fiches désactivées à la fin avec la mention "(inactif)"
  const employeeOptions = useMemo(() => {
    const items = (employeesData?.items ?? []).filter(
      (emp) => !teamFilter || emp.activity === teamFilter,
    );
    return [...items.filter((e) => e.is_active), ...items.filter((e) => !e.is_active)];
  }, [employeesData, teamFilter]);
  const { data: readersData } = useReaders();

  const deleteMutation = useDeleteTimeLog();

  const handleExport = async () => {
    // L'endpoint exige le header Authorization → on ne peut pas utiliser window.open,
    // on télécharge via fetch puis blob.
    const token = localStorage.getItem('token');
    const url = timeLogsApi.exportUrl({
      date_from: dateFrom || undefined,
      date_to: dateTo || undefined,
      employee_id: employeeId,
    });
    const res = await fetch(url, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) {
      alert("Erreur lors de l'export CSV");
      return;
    }
    const blob = await res.blob();
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = `pointages_${dateFrom || 'all'}_${dateTo || 'all'}.csv`;
    a.click();
    URL.revokeObjectURL(objectUrl);
  };

  const handleDelete = (log: TimeLog) => {
    if (window.confirm(`Supprimer ce pointage de ${log.employee.first_name} ${log.employee.last_name} ?`)) {
      deleteMutation.mutate(log.id);
    }
  };

  const columns: Column<TimeLog>[] = [
    {
      key: 'timestamp',
      header: 'Date/Heure',
      render: (log) => (
        <span className="text-gray-700">
          {formatDate(log.timestamp)}{' '}
          <span className="text-gray-500">{formatTime(log.timestamp)}</span>
        </span>
      ),
    },
    {
      key: 'employee',
      header: 'Employé',
      render: (log) => `${log.employee.first_name} ${log.employee.last_name}`,
    },
    {
      key: 'team',
      header: 'Équipe',
      render: (log) => (
        <span className="text-xs text-gray-500">
          {ACTIVITY_LABEL[log.employee.activity] ?? log.employee.activity}
        </span>
      ),
    },
    {
      key: 'type',
      header: 'Type',
      render: (log) => (
        <Badge variant={log.type === 'IN' ? 'success' : 'danger'}>
          {log.type === 'IN' ? 'Entrée' : 'Sortie'}
        </Badge>
      ),
    },
    {
      key: 'reader',
      header: 'Borne',
      render: (log) => (
        <span>
          <span className="font-medium">{log.reader.code}</span>
          <span className="text-gray-400 ml-1 text-xs">{log.reader.name}</span>
        </span>
      ),
    },
    {
      key: 'photo',
      header: '',
      render: (log) =>
        log.has_photo ? (
          <button
            onClick={() => setPhotoLog(log)}
            className="p-1 rounded hover:bg-stone-100 text-accent"
            title="Voir la photo prise au pointage"
          >
            <Camera size={14} />
          </button>
        ) : null,
    },
    {
      key: 'actions',
      header: '',
      render: (log) => (
        <div className="flex gap-1">
          <Button variant="ghost" size="sm" onClick={() => setEditingLog(log)}>
            <Pencil size={13} />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => handleDelete(log)}>
            <Trash2 size={13} className="text-red-500" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div>
      <Header
        title="Pointages"
        action={
          <div className="flex gap-2">
            <Button variant="secondary" size="sm" onClick={handleExport}>
              <Download size={14} className="mr-1" /> Export CSV
            </Button>
            <Button size="sm" onClick={() => setCreating(true)}>
              <Plus size={14} className="mr-1" /> Nouveau pointage
            </Button>
          </div>
        }
      />

      {/* Filtres */}
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Du</label>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
            className="rounded-md border border-gray-300 px-2.5 py-1.5 text-sm
                       focus:outline-none focus:ring-1 focus:ring-accent"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Au</label>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
            className="rounded-md border border-gray-300 px-2.5 py-1.5 text-sm
                       focus:outline-none focus:ring-1 focus:ring-accent"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Équipe</label>
          <select
            value={teamFilter}
            onChange={(e) => {
              setTeamFilter(e.target.value as EmployeeActivity | '');
              setEmployeeId(undefined); // l'employé sélectionné peut sortir du filtre
              setPage(1);
            }}
            className="rounded-md border border-gray-300 px-2.5 py-1.5 text-sm
                       focus:outline-none focus:ring-1 focus:ring-accent"
          >
            <option value="">Toutes</option>
            {(Object.keys(ACTIVITY_LABEL) as EmployeeActivity[]).map((a) => (
              <option key={a} value={a}>{ACTIVITY_LABEL[a]}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Employé</label>
          <select
            value={employeeId ?? ''}
            onChange={(e) => { setEmployeeId(e.target.value ? Number(e.target.value) : undefined); setPage(1); }}
            className="rounded-md border border-gray-300 px-2.5 py-1.5 text-sm
                       focus:outline-none focus:ring-1 focus:ring-accent"
          >
            <option value="">Tous</option>
            {employeeOptions.map((emp) => (
              <option key={emp.id} value={emp.id}>
                {emp.first_name} {emp.last_name}
                {emp.activity ? ` — ${ACTIVITY_LABEL[emp.activity]}` : ''}
                {emp.is_active ? '' : ' (inactif)'}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Borne</label>
          <select
            value={readerId ?? ''}
            onChange={(e) => { setReaderId(e.target.value ? Number(e.target.value) : undefined); setPage(1); }}
            className="rounded-md border border-gray-300 px-2.5 py-1.5 text-sm
                       focus:outline-none focus:ring-1 focus:ring-accent"
          >
            <option value="">Toutes</option>
            {readersData?.map((r) => (
              <option key={r.id} value={r.id}>{r.code} — {r.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Type</label>
          <select
            value={punchType ?? ''}
            onChange={(e) => { setPunchType(e.target.value || undefined); setPage(1); }}
            className="rounded-md border border-gray-300 px-2.5 py-1.5 text-sm
                       focus:outline-none focus:ring-1 focus:ring-accent"
          >
            <option value="">Tous</option>
            <option value="IN">Entrée</option>
            <option value="OUT">Sortie</option>
          </select>
        </div>
      </div>

      {/* Tableau */}
      <Card>
        <Table
          columns={columns}
          data={data?.items ?? []}
          loading={isLoading}
          emptyMessage="Aucun pointage trouvé"
        />
        {data && (
          <Pagination
            page={data.page}
            pages={data.pages}
            total={data.total}
            onPageChange={setPage}
          />
        )}
      </Card>

      {/* Modal édition / création */}
      {(editingLog || creating) && (
        <TimeLogFormModal
          log={editingLog}
          employees={employeesData?.items ?? []}
          readers={readersData ?? []}
          onClose={() => {
            setEditingLog(null);
            setCreating(false);
          }}
        />
      )}

      {/* Modal photo de pointage (anti-fraude) */}
      {photoLog && (
        <PhotoModal log={photoLog} onClose={() => setPhotoLog(null)} />
      )}
    </div>
  );
}

/** Modale d'affichage d'une photo de pointage.
 * Charge l'image en tant que Blob via fetch authentifié (l'endpoint exige un JWT)
 * et la sert au navigateur via createObjectURL.
 */
function PhotoModal({ log, onClose }: { log: TimeLog; onClose: () => void }) {
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    let cancelled = false;
    let blobUrl: string | null = null;
    fetch(`${import.meta.env.VITE_API_URL}/api/time-logs/${log.id}/photo`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then((res) => {
        if (!res.ok) throw new Error('not ok');
        return res.blob();
      })
      .then((blob) => {
        if (cancelled) return;
        blobUrl = URL.createObjectURL(blob);
        setUrl(blobUrl);
      })
      .catch(() => !cancelled && setError(true));
    return () => {
      cancelled = true;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [log.id]);

  return (
    <Modal
      open
      onClose={onClose}
      title={`Photo de pointage · ${log.employee.first_name} ${log.employee.last_name}`}
      footer={<Button onClick={onClose}>Fermer</Button>}
    >
      <div className="space-y-3">
        <p className="text-xs text-stone-600">
          {formatDate(log.timestamp)} {formatTime(log.timestamp)} ·{' '}
          {log.type === 'IN' ? 'Entrée' : 'Sortie'} sur {log.reader.name}
        </p>
        {error ? (
          <p className="text-sm text-rose-600">Photo introuvable ou expirée.</p>
        ) : url ? (
          <img src={url} alt="Photo de pointage" className="w-full rounded-lg border border-stone-200" />
        ) : (
          <p className="text-sm text-stone-400">Chargement…</p>
        )}
        <p className="text-[11px] text-stone-500 italic">
          Photo conservée 90 jours, puis purgée automatiquement.
        </p>
      </div>
    </Modal>
  );
}

// --- Modal de création / édition ---

interface FormProps {
  log: TimeLog | null;
  employees: { id: number; first_name: string; last_name: string }[];
  readers: { id: number; code: string; name: string }[];
  onClose: () => void;
}

function TimeLogFormModal({ log, employees, readers, onClose }: FormProps) {
  const isEdit = !!log;

  // État du formulaire
  const [employeeId, setEmployeeId] = useState(log?.employee.id ?? 0);
  const [readerId, setReaderId] = useState(log?.reader.id ?? readers[0]?.id ?? 0);
  const [type, setType] = useState<'IN' | 'OUT'>(log?.type ?? 'IN');
  const [date, setDate] = useState(
    log?.timestamp ? format(parseServerDate(log.timestamp), 'yyyy-MM-dd') : format(new Date(), 'yyyy-MM-dd')
  );
  const [time, setTime] = useState(
    log?.timestamp ? format(parseServerDate(log.timestamp), 'HH:mm') : '08:00'
  );
  const [error, setError] = useState('');

  const createMutation = useCreateTimeLog();
  const updateMutation = useUpdateTimeLog();

  const handleSubmit = async () => {
    setError('');
    // L'utilisateur saisit l'heure locale ; on convertit en UTC pour stockage.
    const timestamp = new Date(`${date}T${time}:00`).toISOString();

    try {
      if (isEdit && log) {
        await updateMutation.mutateAsync({
          id: log.id,
          data: { reader_id: readerId, type, timestamp },
        });
      } else {
        if (!employeeId) {
          setError('Sélectionnez un employé');
          return;
        }
        await createMutation.mutateAsync({
          employee_id: employeeId,
          reader_id: readerId,
          type,
          timestamp,
        });
      }
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur');
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={isEdit ? 'Modifier le pointage' : 'Nouveau pointage'}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>Annuler</Button>
          <Button onClick={handleSubmit} loading={createMutation.isPending || updateMutation.isPending}>
            {isEdit ? 'Modifier' : 'Créer'}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        {!isEdit && (
          <div className="space-y-1">
            <label className="block text-xs font-medium text-gray-600">Employé</label>
            <select
              value={employeeId}
              onChange={(e) => setEmployeeId(Number(e.target.value))}
              className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm
                         focus:outline-none focus:ring-1 focus:ring-accent"
            >
              <option value={0}>— Choisir —</option>
              {employees.map((emp) => (
                <option key={emp.id} value={emp.id}>
                  {emp.first_name} {emp.last_name}
                </option>
              ))}
            </select>
          </div>
        )}

        {isEdit && log && (
          <p className="text-sm text-gray-600">
            Employé : <span className="font-medium text-gray-900">
              {log.employee.first_name} {log.employee.last_name}
            </span>
          </p>
        )}

        <div className="space-y-1">
          <label className="block text-xs font-medium text-gray-600">Type</label>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setType('IN')}
              className={`flex-1 px-3 py-1.5 rounded-md text-sm ${type === 'IN' ? 'bg-green-100 text-green-700 border border-green-200' : 'bg-gray-50 text-gray-600 border border-gray-200'}`}
            >
              Entrée
            </button>
            <button
              type="button"
              onClick={() => setType('OUT')}
              className={`flex-1 px-3 py-1.5 rounded-md text-sm ${type === 'OUT' ? 'bg-red-100 text-red-700 border border-red-200' : 'bg-gray-50 text-gray-600 border border-gray-200'}`}
            >
              Sortie
            </button>
          </div>
        </div>

        <div className="space-y-1">
          <label className="block text-xs font-medium text-gray-600">Borne</label>
          <select
            value={readerId}
            onChange={(e) => setReaderId(Number(e.target.value))}
            className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm
                       focus:outline-none focus:ring-1 focus:ring-accent"
          >
            {readers.map((r) => (
              <option key={r.id} value={r.id}>{r.code} — {r.name}</option>
            ))}
          </select>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <label className="block text-xs font-medium text-gray-600">Date</label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm
                         focus:outline-none focus:ring-1 focus:ring-accent"
            />
          </div>
          <div className="space-y-1">
            <label className="block text-xs font-medium text-gray-600">Heure</label>
            <input
              type="time"
              value={time}
              onChange={(e) => setTime(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm
                         focus:outline-none focus:ring-1 focus:ring-accent"
            />
          </div>
        </div>

        {error && (
          <p className="text-xs text-red-500">{error}</p>
        )}
      </div>
    </Modal>
  );
}
